import _ from "lodash";
import {
  getDepartments,
  getDepartmentFunction,
  getManagementLevels,
} from "@/api/people";
import {
  getCompanyList,
  getLocationList,
  getCompanySize,
  getIndustry,
  getIndustryCategory,
  getTechList,
  getTechCategoryList,
  getMediaList,
  getRevenue,
  getVerticalCategory,
} from "@/api/company";
import {
  getCurrenices,
  getPriceConfig,
  getSubscriptions,
  upgradePlan,
} from "@/api/setting";
import { getAd } from "@/api/website-intent";
import {
  getUrlHashAndRecallApi,
  SUBSCRIPTION_CELEBRATION_KEY,
} from "@/utils";
import { purchaseCredit } from "@/api/account";
import { getSequenceList } from "@/api/sequence";
import { getGalleries, getCategories } from "@/api/home/market";
import { getIsAccessDisabled } from "@/utils/permissions";
import Vue from "vue";

// Stable per-attempt idempotency token for plan upgrades (see changePlan) — reused across resubmits, reset on intent change / success. Module-scoped (in-memory, per page session): a private dedup token, not reactive app state.
let upgradePlanIdempotency = null;

const state = {
  // Set on any successful plan change; SubscriptionWelcomePopup watches it.
  subscriptionCelebration: null,

  home_market_update_news_access_code: null,
  home_market_update_job_postings_access_code: null,
  home_market_update_ads_access_code: null,
  home_trending_companies_access_code: null,

  managementLevels: [],
  managementLoading: false,
  managementPromise: null,

  departments: [],
  departmentLoading: false,
  departmentPromise: null,

  departmentFunctions: [],
  departmentFunctionLoading: false,
  departmentFunctionPromise: null,

  companyLists: [],
  originCompanyList: [],
  companyListLoading: false,

  companyListMap: new Map(),
  originCompanyListMap: new Map(),
  companyListPromise: null,

  locationList: [],
  locationLoading: false,
  locationListPromise: null,
  locationListMap: new Map(),

  companySizeLoading: false,
  companySize: [],
  companySizePromise: null,

  techList: [],
  techLoading: false,
  originTechList: [],
  techListPromise: null,

  techCategoryList: [],
  techCategoryLoading: false,
  techCategoryPromise: null,

  verticalsList: [],
  originVerticalsList: [],
  verticalsListLoading: false,
  verticalListPromise: null,

  verticalsCategoryList: [],
  verticalsCategoryLoading: false,
  verticalsCategoryPromise: null,

  mediaList: [],
  mediaLoading: false,
  mediaPromise: null,

  adList: [],
  adLoading: false,
  adPromise: null,

  revenueList: [],
  revenueLoading: [],
  revenuePromise: null,

  sequenceList: [],
  sequenceLoading: false,
  sequenceLoaded: false,
  sequencePromise: null,

  gallaryList: [],
  gallaryLoading: false,
  gallaryPromise: null,

  newsCategoryList: [],
  newsCategoryLoading: false,
  newsCategoryPromise: null,

  verticalCategoryList: [],
  verticalCategoryLoading: false,
  verticalCategoryPromise: null,

  maximumQueryLimitShow: false,
  annualPriceList: [],
  monthPriceList: [],
  monthPriceIndex: -1,
  annualPriceIndex: -1,
  payDialog: false,
  paymentSecurityDialog: false,
  upgradeDialog: false,
  addSequenceDialog: false,
  showNotificationBar: false,
  front_end_config: null,
  default_referral_url_expiration_date_period: null,
  currencies: [],
  contactus_url: "",
  current_subscription_type: "annual",
  sequence_credential_authentication_retry: 0,
  subscriptionLoading: false,

  notEnoughCredits: false,
  notEnoughCreditsConfirmAction: null,
};

const mutations = {
  set_home_market_update_news_access_code(state, payload) {
    state.home_market_update_news_access_code = payload;
  },
  set_home_market_update_job_postings_access_code(state, payload) {
    state.home_market_update_job_postings_access_code = payload;
  },
  set_home_market_update_ads_access_code(state, payload) {
    state.home_market_update_ads_access_code = payload;
  },
  set_home_trending_companies_access_code(state, payload) {
    state.home_trending_companies_access_code = payload;
  },

  // department
  setDepartments(state, payload) {
    state.departments = payload;
    state.departmentPromise = null;
  },
  setDepartmentLoading(state, payload) {
    state.departmentLoading = payload;
  },
  setDepartmentPromise(state, payload) {
    state.departmentPromise = payload;
  },

  // departmentFunction
  setDepartmentFunctions(state, payload) {
    state.departmentFunctions = payload;
    state.departmentFunctionPromise = null;
  },
  setDepartmentFunctionLoading(state, payload) {
    state.departmentFunctionLoading = payload;
  },
  setDepartmentFunctionPromise(state, payload) {
    state.departmentFunctionPromise = payload;
  },

  // management
  setManagementLevels(state, payload) {
    state.managementLevels = payload;
    state.managementPromise = null;
  },
  setManagementLoading(state, payload) {
    state.managementLoading = payload;
  },
  setManagementPromise(state, payload) {
    state.managementPromise = payload;
  },

  setCompanyList(state, payload) {
    state.companyLists = payload;
  },
  setOriginCompanyList(state, payload) {
    state.originCompanyList = payload;
  },

  // company
  setCompanyListMap(state, payload) {
    state.companyListMap = payload;
    state.companyListPromise = null;
  },
  setCompanyListLoading(state, payload) {
    state.companyListLoading = payload;
  },
  setOriginCompanyListMap(state, payload) {
    state.originCompanyListMap = payload;
  },
  setCompanyListMapPromise(state, payload) {
    state.companyListPromise = payload;
  },

  // location
  setLocationList(state, payload) {
    state.locationList = payload;
    state.locationListPromise = null;
  },
  setLocationLoading(state, payload) {
    state.locationLoading = payload;
  },
  setLocationPromise(state, payload) {
    state.locationListPromise = payload;
  },
  setLocationListMap(state, payload) {
    state.locationListMap = payload;
  },

  // company size
  setCompanySizeLoading(state, payload) {
    state.companySizeLoading = payload;
  },
  setCompanySize(state, payload) {
    state.companySize = payload;
    state.companySizePromise = null;
  },
  setCompanySizePromise(state, payload) {
    state.companySizePromise = payload;
  },

  // vertical
  setVerticalsList(state, payload) {
    state.verticalsList = payload;
    state.verticalListPromise = null;
  },
  setOriginVerticalsList(state, payload) {
    state.originVerticalsList = payload;
  },
  setVerticalsListLoading(state, payload) {
    state.verticalsListLoading = payload;
  },
  setVerticalsListPromise(state, payload) {
    state.verticalListPromise = payload;
  },

  // vertical category
  setVerticalsCategoryList(state, payload) {
    state.verticalsCategoryList = payload;
    state.verticalsCategoryPromise = null;
  },
  setVerticalsCategoryLoading(state, payload) {
    state.verticalsCategoryLoading = payload;
  },
  setVerticalsCategoryPromise(state, payload) {
    state.verticalsCategoryPromise = payload;
  },

  // tech
  setTechList(state, payload) {
    state.techList = payload;
    state.techListPromise = null;
  },
  setTechLoading(state, payload) {
    state.techLoading = payload;
  },
  setTechPromise(state, payload) {
    state.techListPromise = payload;
  },
  setOriginTechList(state, payload) {
    state.originTechList = payload;
  },

  // tech category
  setTechCategoryList(state, payload) {
    state.techCategoryList = payload;
    state.techCategoryPromise = null;
  },
  setTechCategoryLoading(state, payload) {
    state.techCategoryLoading = payload;
  },
  setTechCategoryPromise(state, payload) {
    state.techCategoryPromise = payload;
  },

  // media
  setMediaList(state, payload) {
    state.mediaList = payload;
    state.mediaPromise = null;
  },
  setMediaLoading(state, payload) {
    state.mediaLoading = payload;
  },
  setMediaPromise(state, payload) {
    state.mediaPromise = payload;
  },

  //revenue
  setRevenueList(state, payload) {
    state.revenueList = payload;
    state.revenuePromise = null;
  },
  setRevenueLoading(state, payload) {
    state.revenueLoading = payload;
  },
  setRevenuePromise(state, payload) {
    state.mediaPromise = payload;
  },

  // ad
  setAdList(state, payload) {
    state.adList = payload;
    state.adPromise = null;
  },
  setAdLoading(state, payload) {
    state.adLoading = payload;
  },
  setAdPromise(state, payload) {
    state.adPromise = payload;
  },

  // sequence
  setSequenceList(state, payload) {
    state.sequenceList = payload;
    state.sequenceLoaded = true;
    state.sequencePromise = null;
  },
  setSequenceLoading(state, payload) {
    state.sequenceLoading = payload;
  },
  setSequencePromise(state, payload) {
    state.sequencePromise = payload;
  },

  // gallery
  setGallaryList(state, payload) {
    state.gallaryList = payload;
    state.gallaryPromise = null;
  },
  setGallaryLoading(state, payload) {
    state.gallaryLoading = payload;
  },
  setGallaryPromise(state, payload) {
    state.gallaryPromise = payload;
  },

  // news category
  setNewsCategoryList(state, payload) {
    state.newsCategoryList = payload;
    state.newsCategoryPromise = null;
  },
  setNewsCategoryLoading(state, payload) {
    state.newsCategoryLoading = payload;
  },
  setNewsCategoryPromise(state, payload) {
    state.newsCategoryPromise = payload;
  },

  // industry sub category
  setVerticalCategoryList(state, payload) {
    state.verticalCategoryList = payload;
    state.verticalCategoryPromise = null;
  },
  setVerticalCategoryLoading(state, payload) {
    state.verticalCategoryLoading = payload;
  },
  setVerticalCategoryPromise(state, payload) {
    state.verticalCategoryPromise = payload;
  },

  setMaximumQueryLimitShow(state) {
    state.maximumQueryLimitShow = !state.maximumQueryLimitShow;
  },
  setMonthPriceList(state, payload) {
    state.monthPriceList = payload;
  },
  setAnnualPriceList(state, payload) {
    state.annualPriceList = payload;
  },
  setMonthPriceIndex(state, payload) {
    state.monthPriceIndex = payload;
  },
  setAnnualPriceIndex(state, payload) {
    state.annualPriceIndex = payload;
  },
  setSubscriptionCelebration(state, payload) {
    state.subscriptionCelebration = payload;
  },
  setPayDialog(state, payload) {
    state.payDialog = payload;
  },
  setPaymentSecurityDialog(state, payload) {
    state.paymentSecurityDialog = payload;
  },
  setNotEnoughCredits(state, payload) {
    state.notEnoughCredits = payload;
  },
  setNotEnoughCreditsConfirmAction(state, action) {
    state.notEnoughCreditsConfirmAction = action;
  },
  setUpgradeDialog(state, payload) {
    state.upgradeDialog = payload;
  },
  setSequenceDialog(state) {
    state.addSequenceDialog = !state.addSequenceDialog;
  },
  setNotificationBar(state, payload) {
    state.showNotificationBar = payload;
  },
  setFrontEndConfig(state, payload) {
    state.front_end_config = payload;
  },
  set_default_referral_url_expiration_date_period(state, payload) {
    localStorage.setItem(
      "default_referral_url_expiration_date_period",
      payload,
    );
    state.default_referral_url_expiration_date_period = payload;
  },
  setCurrencies(state, payload) {
    state.currencies = payload;
  },
  setContactUsURL(state, payload) {
    state.contactus_url = payload;
  },
  set_current_subscription_type(state, payload) {
    state.current_subscription_type = payload;
  },
  add_sequence_credential_authentication_retry(state) {
    state.sequence_credential_authentication_retry =
      state.sequence_credential_authentication_retry + 1;
  },
  setSubscriptionLoading(state, payload) {
    state.subscriptionLoading = payload;
  },
};

const actions = {
  /*-------------切换语言时获取全部----------------*/
  async getAllSelectList({ commit, dispatch }, data) {
    try {
      dispatch("getDepartments", { forceRefresh: true });
      dispatch("getDepartmentFunctions", { forceRefresh: true });
      dispatch("getManagementLevels", { forceRefresh: true });
      dispatch("getCompanyList");
      dispatch("getLocationList", { forceRefresh: true });
      dispatch("getCompanySize", { forceRefresh: true });
      dispatch("getIndustry", { forceRefresh: true });
      dispatch("getIndustryCategory", { forceRefresh: true });
      dispatch("getTechList", { forceRefresh: true });
      dispatch("getCategoryList", { forceRefresh: true });
      dispatch("getMediaList", { forceRefresh: true });
      dispatch("getRevenue", { forceRefresh: true });
      dispatch("getAdList", { forceRefresh: true });
      dispatch("getVerticalCategoryList", { forceRefresh: true });
      dispatch("getNewsCategorysList", { forceRefresh: true });
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  /*-------------人员部门----------------*/
  async getDepartments({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.departments.length > 0 && !forceRefresh) {
      return Promise.resolve(state.departments);
    }

    if (state.departmentPromise && !forceRefresh) {
      return state.departmentPromise;
    }

    const promise = getDepartments()
      .then((result) => {
        if (result && result.data) {
          commit("setDepartments", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setDepartmentPromise", null);
      })
      .finally(() => {
        commit("setDepartmentLoading", false);
      });

    commit("setDepartmentPromise", promise);
    commit("setDepartmentLoading", true);
    return promise;
  },
  /*-------------部门Function----------------*/
  async getDepartmentFunctions({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.departmentFunctions.length > 0 && !forceRefresh) {
      return Promise.resolve(state.departmentFunctions);
    }

    if (state.departmentFunctionPromise && !forceRefresh) {
      return state.departmentFunctionPromise;
    }

    const promise = getDepartmentFunction()
      .then((result) => {
        if (result && result.data) {
          commit("setDepartmentFunctions", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setDepartmentFunctionPromise", null);
      })
      .finally(() => {
        commit("setDepartmentFunctionLoading", false);
      });

    commit("setDepartmentFunctionPromise", promise);
    commit("setDepartmentFunctionLoading", true);
    return promise;
  },
  /*-------------人员管理级别----------------*/
  getManagementLevels({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.managementLevels.length > 0 && !forceRefresh) {
      return Promise.resolve(state.managementLevels);
    }

    if (state.managementPromise && !forceRefresh) {
      return state.managementPromise;
    }

    const promise = getManagementLevels()
      .then((result) => {
        if (result && result.data) {
          commit("setManagementLevels", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setManagementPromise", null);
      })
      .finally(() => {
        commit("setManagementLoading", false);
      });

    commit("setManagementPromise", promise);
    commit("setManagementLoading", true);
    return promise;
  },

  // industry sub category
  getVerticalCategoryList({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.verticalCategoryList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.verticalCategoryList);
    }

    if (state.verticalCategoryPromise && !forceRefresh) {
      return state.verticalCategoryPromise;
    }

    const promise = getVerticalCategory()
      .then((result) => {
        if (result && result.data) {
          commit("setVerticalCategoryList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setVerticalCategoryPromise", null);
      })
      .finally(() => {
        commit("setVerticalCategoryLoading", false);
      });

    commit("setVerticalCategoryPromise", promise);
    commit("setVerticalCategoryLoading", true);
    return promise;
  },
  /*---------------推荐公司列表----------------------*/
  async getCompanyList(context, val) {
    try {
      const result = await getCompanyList({ company_name: val });
      if (result && result.data) {
        context.commit("setCompanyList", result.data);
        if (!val) {
          context.commit("setOriginCompanyList", result.data);
        }
        return result.data;
      }
    } catch (err) {
      console.log(err);
      throw err;
    }
  },

  getCompanyListMap({ commit, state }, data = {}) {
    const { forceRefresh = false, ...params } = data || {};

    if (Object.keys(params).length > 0) {
      commit("setCompanyListLoading", true);

      const promise = getCompanyList(params)
        .then((result) => {
          if (result && result.data) {
            const companyMap = new Map();
            result.data.forEach((company) =>
              companyMap.set(company.domain_search_id, company),
            );
            commit("setCompanyListMap", companyMap);
            if (Object.keys(params).length === 0) {
              commit("setOriginCompanyListMap", companyMap);
            }
            commit("setCompanyListLoading", false);
            return companyMap;
          }
        })
        .catch(() => {
          commit("setCompanyListMapPromise", null);
        })
        .finally(() => {
          commit("setCompanyListLoading", false);
        });

      return promise;
    }

    if (state.companyListMap.size > 0 && !forceRefresh) {
      return Promise.resolve(state.companyListMap);
    }

    if (state.companyListPromise && !forceRefresh) {
      return state.companyListPromise;
    }

    const promise = getCompanyList()
      .then((result) => {
        if (result && result.data) {
          const companyMap = new Map();
          result.data.forEach((company) =>
            companyMap.set(company.domain_search_id, company),
          );
          commit("setCompanyListMap", companyMap);
          if (Object.keys(params).length === 0) {
            commit("setOriginCompanyListMap", companyMap);
          }
          commit("setCompanyListLoading", false);
          return companyMap;
        }
      })
      .catch(() => {
        commit("setCompanyListMapPromise", null);
      })
      .finally(() => {
        commit("setCompanyListLoading", false);
      });

    commit("setCompanyListMapPromise", promise);
    commit("setCompanyListLoading", true);
    return promise;
  },

  getLocationList({ commit, state }, data = {}) {
    const { forceRefresh = false, ...rest } = data || {};
    if (state.locationList.size > 0 && !forceRefresh) {
      return Promise.resolve(state.locationListMap);
    }

    if (state.locationListPromise && !forceRefresh) {
      return state.locationListPromise;
    }

    const promise = getLocationList()
      .then((result) => {
        if (result && result.data) {
          commit("setLocationList", result.data);

          const locationMap = new Map();
          result.data.forEach((item) => {
            locationMap.set(item.iso_code, {
              label: item.location_name,
              value: item.iso_code,
              region: item.business_region || "",
            });
          });
          commit("setLocationListMap", locationMap);

          return locationMap;
        }
        return new Map();
      })
      .catch((err) => {
        commit("setLocationPromise", null);
        return Promise.reject(err);
      })
      .finally(() => {
        commit("setLocationLoading", false);
      });

    commit("setLocationPromise", promise);
    commit("setLocationLoading", true);
    return promise;
  },

  getCompanySize({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.companySize.length > 0 && !forceRefresh) {
      return Promise.resolve(state.companySize);
    }

    if (state.companySizePromise && !forceRefresh) {
      return state.companySizePromise;
    }

    const promise = getCompanySize()
      .then((result) => {
        if (result && result.data) {
          commit("setCompanySize", result.data);
        }
      })
      .catch(() => {
        commit("setCompanySizePromise", null);
      })
      .finally(() => {
        commit("setCompanySizeLoading", false);
      });

    commit("setCompanySizePromise", promise);
    commit("setCompanySizeLoading", true);
    return promise;
  },

  getIndustry({ commit, state }, data = {}) {
    const { forceRefresh = false, ...rest } = data || {};

    if (JSON.stringify(rest) !== "{}") {
      commit("setVerticalsListLoading", true);

      const promise = getIndustry(rest)
        .then((result) => {
          if (result && result.data) {
            commit("setVerticalsList", result.data);
            return result.data;
          }
          return [];
        })
        .catch((err) => {
          commit("setVerticalsList", []);
          throw err;
        })
        .finally(() => {
          commit("setVerticalsListLoading", false);
        });

      return promise;
    }

    if (state.verticalsList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.verticalsList);
    }

    if (state.verticalListPromise && !forceRefresh) {
      return state.verticalListPromise;
    }

    const promise = getIndustry(rest)
      .then((result) => {
        if (result && result.data) {
          commit("setVerticalsList", result.data);
          if (!rest?.keyword) {
            commit("setOriginVerticalsList", result.data);
          }
          return result.data;
        }
      })
      .catch(() => {
        commit("setVerticalsListPromise", null);
      })
      .finally(() => {
        commit("setVerticalsListLoading", false);
      });

    commit("setVerticalsListPromise", promise);
    commit("setVerticalsListLoading", true);
    return promise;
  },

  getIndustryCategory({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.verticalCategoryList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.verticalCategoryList);
    }

    if (state.verticalsCategoryPromise && !forceRefresh) {
      return state.verticalsCategoryPromise;
    }

    const promise = getIndustryCategory()
      .then((result) => {
        if (result && result.data) {
          commit("setVerticalsCategoryList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setVerticalsCategoryPromise", null);
      })
      .finally(() => {
        commit("setVerticalsCategoryLoading", false);
      });

    commit("setVerticalsCategoryPromise", promise);
    commit("setVerticalsCategoryLoading", true);
    return promise;
  },

  getTechList({ commit, state }, data = {}) {
    const { forceRefresh = false, ...rest } = data || {};
    if (JSON.stringify(rest) !== "{}") {
      commit("setTechLoading", true);
      const promise = getTechList(rest)
        .then((result) => {
          if (result && result.data) {
            commit("setTechList", result.data || []);
            return result.data;
          }
          return [];
        })
        .catch((err) => {
          commit("setTechList", []);
          throw err;
        })
        .finally(() => {
          commit("setTechLoading", false);
        });

      return promise;
    }

    if (state.techList?.length > 0 && !forceRefresh) {
      return Promise.resolve(state.techList);
    }

    if (state.techListPromise && !forceRefresh) {
      return state.techListPromise;
    }

    const promise = getTechList(rest)
      .then((result) => {
        if (result && result.data) {
          commit("setTechList", result.data);
          if (!rest?.keyword) {
            commit("setOriginTechList", result.data);
            return result.data;
          }
        }
      })
      .catch(() => {
        commit("setTechPromise", null);
      })
      .finally(() => {
        commit("setTechLoading", false);
      });

    commit("setTechPromise", promise);
    commit("setTechLoading", true);
    return promise;
  },

  getCategoryList({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.techCategoryList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.techCategoryList);
    }

    if (state.techCategoryPromise && !forceRefresh) {
      return state.techCategoryPromise;
    }

    const promise = getTechCategoryList()
      .then((result) => {
        if (result && result.data) {
          commit("setTechCategoryList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setTechCategoryPromise", null);
      })
      .finally(() => {
        commit("setTechCategoryLoading", false);
      });

    commit("setTechCategoryPromise", promise);
    commit("setTechCategoryLoading", true);
    return promise;
  },

  getMediaList({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.mediaList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.mediaList);
    }

    if (state.mediaPromise && !forceRefresh) {
      return state.mediaPromise;
    }

    const promise = getMediaList()
      .then((result) => {
        if (result && result.data) {
          commit("setMediaList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setMediaPromise", null);
      })
      .finally(() => {
        commit("setMediaLoading", false);
      });

    commit("setMediaPromise", promise);
    commit("setMediaLoading", true);
    return promise;
  },

  getRevenue({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.revenueList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.revenueList);
    }

    if (state.revenuePromise && !forceRefresh) {
      return state.revenuePromise;
    }

    const promise = getRevenue()
      .then((result) => {
        if (result && result.data) {
          commit("setRevenueList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setRevenuePromise", null);
      })
      .finally(() => {
        commit("setRevenueLoading", false);
      });

    commit("setRevenuePromise", promise);
    commit("setRevenueLoading", true);
    return promise;
  },

  getAdList({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.adList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.adList);
    }

    if (state.adPromise && !forceRefresh) {
      return state.adPromise;
    }

    const promise = getAd()
      .then((result) => {
        if (result && result.data) {
          commit("setAdList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setAdPromise", null);
      })
      .finally(() => {
        commit("setAdLoading", false);
      });

    commit("setAdPromise", promise);
    commit("setAdLoading", true);
    return promise;
  },

  async getNewsGalleryList({ commit, state }) {
    try {
      state.gallaryLoading = true;
      const { data } = await getGalleries();
      commit("setGallaryList", data);
    } catch (err) {
    } finally {
      state.gallaryLoading = false;
    }
  },

  async getNewsCategorysList({ commit, state }, data) {
    const { forceRefresh = false, ...rest } = data || {};

    if (state.newsCategoryList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.newsCategoryList);
    }

    if (state.newsCategoryPromise && !forceRefresh) {
      return state.newsCategoryPromise;
    }

    const promise = getCategories()
      .then((result) => {
        if (result && result.data) {
          commit("setNewsCategoryList", result.data);
          return result.data;
        }
      })
      .catch(() => {
        commit("setNewsCategoryPromise", null);
      })
      .finally(() => {
        commit("setNewsCategoryLoading", false);
      });

    commit("setNewsCategoryPromise", promise);
    commit("setNewsCategoryLoading", true);
    return promise;
  },

  async getSequenceList({ commit, state, rootState }, data) {
    const sequence_list_access_code =
      rootState?.sequence?.sequence_list_access_code;
    if (getIsAccessDisabled(sequence_list_access_code)) {
      return;
    }
    const { forceRefresh = false, ...rest } = data || {};

    if (state.sequenceList.length > 0 && !forceRefresh) {
      return Promise.resolve(state.sequenceList);
    }

    if (state.sequencePromise && !forceRefresh) {
      return state.sequencePromise;
    }

    const promise = getSequenceList({
      profile_id: rootState.user.currentId.profile_id,
      is_full_page_search: true,
    })
      .then((result) => {
        if (result && result.data) {
          commit("setSequenceList", result.data.sequences);
          return result.data.sequences;
        }
      })
      .catch((error) => {
        commit("setSequencePromise", null);
        const code = error?.response?.data?.code;

        commit("sequence/set_sequence_list_access_code", +code, {
          root: true,
        });
      })
      .finally(() => {
        commit("setSequenceLoading", false);
      });

    commit("setSequencePromise", promise);
    commit("setSequenceLoading", true);
    return promise;
  },

  async getSubscription({ commit, rootState }) {
    try {
      if (getIsAccessDisabled(rootState.user.subscription_access_code)) {
        return;
      }
      commit("setSubscriptionLoading", true);
      const data = await getSubscriptions({
        profile_id: this.state.user.currentId.profile_id,
      });
      const monthList = [];
      const yearList = [];
      commit("setContactUsURL", data.metadata.contactus_url);
      data?.data?.map((item) => {
        if (item.is_current) {
          commit(
            "set_current_subscription_type",
            item.is_annual ? "annual" : "monthly",
          );
        }
        if (item.is_default || item.is_custom_plan) {
          yearList.push(item);
          monthList.push(item);
        } else {
          if (item.is_annual) {
            item.is_current && commit("setAnnualPriceIndex", yearList.length);
            yearList.push(item);
          } else {
            item.is_current && commit("setMonthPriceIndex", monthList.length);
            monthList.push(item);
          }
        }
      });
      commit("setAnnualPriceList", yearList);
      commit("setMonthPriceList", monthList);
    } catch (error) {
      console.log(error);
      const code = error?.response?.data?.code;

      commit("user/set_subscription_access_code", +code, { root: true });
      throw error;
    } finally {
      commit("setSubscriptionLoading", false);
    }
  },
  recallApi({ commit }, payload) {
    const data = getUrlHashAndRecallApi();
    if (data) {
      if (data.success) {
        let bathPath;
        if (data.page === "/settings/billing?tab=3") {
          bathPath = data.page;
        } else {
          bathPath = data.page?.split("?")
            ? data.page.split("?")[0]
            : data.page;
        }
        if (bathPath === payload.path) {
          payload.cb && payload.cb(data.name, data.params, true);
        }
      } else if (data.message) {
        Vue.$toast.error(data.message);
        localStorage.getItem("CbApiInfo") &&
          localStorage.removeItem("CbApiInfo");
        // Failed 3DS return: drop the token so the next try is a fresh
        // Stripe attempt, not a replay against the dead intent.
        upgradePlanIdempotency = null;
      }
      return true;
    } else {
      return false;
    }
  },
  async changePlan({ commit, state }, data) {
    try {
      // Stable per-attempt key: reused across resubmits of the same target+promo so a retry can't double-charge across a minute boundary; regenerated when the intent changes (so Stripe never sees one key with different params); cleared on success. Recall (3DS resume) replays the original saved body untouched.
      const idem_sig = `${data?.info?.subscription_identifier}|${data?.info?.promotion_code || ""}`;
      if (!upgradePlanIdempotency || upgradePlanIdempotency.sig !== idem_sig) {
        upgradePlanIdempotency = {
          sig: idem_sig,
          key: `upgrade:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`,
        };
      }
      const params = {
        profile_id: this.state.user.currentId.profile_id,
        subscription_identifier: data?.info?.subscription_identifier,
        redirect_path: "/#" + data?.info.path,
        promotion_code: data?.info?.promotion_code || "",
        idempotency_key: upgradePlanIdempotency.key,
      };
      const result = await upgradePlan(data.recall ? data.info : params);
      upgradePlanIdempotency = null;
      // Success feedback: welcome popup for upgrades (sessionStorage survives
      // reload flows; the commit covers in-place flows), neutral toast for
      // pricing-page downgrades — "workspace unlocked" copy would be wrong.
      try {
        const info = data?.info && typeof data.info === "object" ? data.info : {};
        const isDowngrade = info.discount_rate != null && !(info.discount_rate > 0);
        if (isDowngrade && !data.is_defer_celebration) {
          Vue.$toast.success("change successfully");
        } else {
          const celebration = {
            name: info.upgrade_subscription_name || info.name || "",
            credit: info.credit || info.total_credit_refill || null,
          };
          window.sessionStorage.setItem(
            SUBSCRIPTION_CELEBRATION_KEY,
            JSON.stringify(celebration),
          );
          // is_defer_celebration = caller reloads right after paying; the popup
          // must fire from sessionStorage post-reload, not from this commit.
          if (!data.is_defer_celebration) {
            commit("setSubscriptionCelebration", celebration);
          }
        }
      } catch (e) {
        // Decorative only — never block the payment result.
      }
      data.recall &&
        localStorage.getItem("CbApiInfo") &&
        localStorage.removeItem("CbApiInfo");
      return result;
    } catch (err) {
      if (err.response && err.response.data.code) {
        if (err.response.data.code === 40208) {
          commit("setPaymentSecurityDialog", true);
        } else if (err.response.data.code === 40201) {
          if (state.maximumQueryLimitShow) {
            commit("setMaximumQueryLimitShow", false);
          }
          // commit('setPayDialog', true) // request里处理了
        } else if (err.response.data.code === 40210) {
          localStorage.setItem(
            "CbApiInfo",
            JSON.stringify({
              page: data?.info.path,
              name: "changePlan",
              params: err.response.config.data,
            }),
          );
          window.location.href =
            err.response.data.details.next_action.redirect_to_url;
        } else {
          // 40211: "The transaction has been completed before"
          // 40901
          Vue.$toast.error(err.response.data);
        }
      }
    }
  },
  async topUp({ commit, state }, data) {
    try {
      const params = {
        profile_id: this.state.user.currentId.profile_id,
        credits: this.state.user.systemConfig.min_credit_topup,
        redirect_path: "/#" + data?.info.path,
      };
      const result = await purchaseCredit(data.recall ? data.info : params);
      Vue.$toast.success("topup successfully");
      data.recall &&
        localStorage.getItem("CbApiInfo") &&
        localStorage.removeItem("CbApiInfo");
      return result;
    } catch (err) {
      if (err.response.data.code === 40210) {
        localStorage.setItem(
          "CbApiInfo",
          JSON.stringify({
            page: data?.info.path,
            name: "topUp",
            params: err.response.config.data,
          }),
        );
        window.location.href =
          err.response.data.details.next_action.redirect_to_url;
      }
    }
  },
  async queryFrontEndConfig({ commit, state }, data) {
    try {
      const res = await getPriceConfig();
      if (res) {
        commit("setFrontEndConfig", res);
      }
      return res;
    } catch (error) {
      console.log(error);
      if (error?.code !== "ERR_CANCELED") {
        throw error;
      }
    }
  },
  async queryCurrencies({ commit }, data) {
    try {
      const res = await getCurrenices();
      if (res?.data) {
        commit("setCurrencies", res.data || []);
      }
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") {
        throw error;
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
